import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import { Button, CircularProgress } from '@mui/material';

export type FavouriteButtonProps = {
  isFavourited: boolean;
  handleAddFavouriteClick: () => void;
  handleRemoveFavouriteClick: () => void;
  addFavouriteButtonLabel: string;
  removeFavouriteButtonLabel: string;
  isLoading?: boolean;
  testId?: string;
};

/**
 * FavouriteButton component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @param {boolean} isFavourited - Whether the button is favourited.
 * @param {function} handleAddFavouriteClick - The function to handle the add favourite click.
 * @param {function} handleRemoveFavouriteClick - The function to handle the remove favourite click.
 * @param {string} addFavouriteButtonLabel - The label for the add favourite button.
 * @param {string} removeFavouriteButtonLabel - The label for the remove favourite button.
 * @param {boolean} [isLoading] - Whether the button is loading.
 * @param {string} [testId] - The test id for the button.
 */

export const FavouriteButton = ({
  isFavourited,
  handleAddFavouriteClick,
  handleRemoveFavouriteClick,
  addFavouriteButtonLabel,
  removeFavouriteButtonLabel,
  isLoading,
  testId,
}: FavouriteButtonProps) => {
  if (isLoading) {
    return <CircularProgress color="primary" size="2rem" />;
  }

  return (
    <Button
      color="primary"
      endIcon={isFavourited ? <Favorite /> : <FavoriteBorder />}
      onClick={
        isFavourited ? handleRemoveFavouriteClick : handleAddFavouriteClick
      }
      data-testid={testId || 'favourites-button'}
      data-favourite-status={isFavourited ? 'favourited' : 'not-favourited'}
    >
      {isFavourited ? removeFavouriteButtonLabel : addFavouriteButtonLabel}
    </Button>
  );
};
